/**
 * Brand configuration for quote pages.
 * All brand-specific values live here. Components receive brandConfig as a prop.
 * To port to other brands (Rancho, Cody's, Splash), copy components and swap this config.
 *
 * The serviceAgreement object lives in src/_lib/agreement-clauses.js so
 * the submit handler (api/quote/submit.js) can import the same source
 * of truth and persist a snapshot of the exact clauses the customer
 * signed at sign time.
 */

import { SERVICE_AGREEMENT } from '../_lib/agreement-clauses.js';
import {
  BRAND_MANIFEST,
  DEFAULT_BRAND as MANIFEST_DEFAULT,
  resolveBrandKey as manifestResolveBrandKey,
  resolveBrandFromHost,
  validateBrandParam,
} from './brand-manifest.js';

const cabanaConfig = {
  name: 'Cabana',
  tagline: 'Weekly Pool Service by Cabana',
  logo: '/images/logo.svg',
  heroImage: '/images/Cover.png',
  phone: '(888) 817-4115',
  website: 'cabanapools.com',

  colors: {
    primary: '#27A2D6',
    primaryDark: '#1e8bb8',
    accent: '#30B130',
    textPrimary: '#000000',
    textSecondary: '#00000080',
    background: '#FFFFFF',
    surfaceLight: '#F8FAFB',
    border: '#ECECEC',
  },

  trustBadges: [
    { icon: '/images/technician-icon.svg', label: 'CPO Certified' },
    { icon: '/images/guarantee-icon.svg', label: '7-Day Support' },
    { icon: '/images/cancel-anytime.svg', label: 'Cancel Anytime' },
    { icon: '/images/insurance-icon.svg', label: '$2M Insured' },
  ],

  features: [
    {
      icon: '/images/technician-icon.svg',
      title: 'Certified Technicians (CPO)',
      description: 'All techs are Certified Pool Operators (CPOs) by the Pool & Hot Tub Alliance (PHTA).',
    },
    {
      icon: '/images/guarantee-icon.svg',
      title: 'World Class Support',
      description: 'Our support team is available 7 days a week to help with any questions or concerns.',
    },
    {
      icon: '/images/cancel-anytime.svg',
      title: 'Cancel Anytime',
      description: "We don't lock you into any contracts and you can cancel at any time.",
    },
    {
      icon: '/images/insurance-icon.svg',
      title: 'Bonded & Insured',
      description: 'We carry a multimillion dollar liability insurance policy so you\'re fully covered.',
    },
  ],

  serviceIncludes: [
    'Test and balance water chemistry',
    'Skim surface debris',
    'Brush walls and steps as needed',
    'Vacuum pool surfaces',
    'Empty skimmer and pump baskets',
    'Inspect equipment for issues',
    'Backwash filter when needed',
    'Photo report emailed to you',
  ],

  serviceIncludesByTier: {
    // Each tier mirrors the `full` list with the items the tier doesn't
    // cover removed, so customers see the same wording across tiers and
    // can spot exactly what changes by paying for a higher tier.
    // Order matters: rendered in a 2-column auto-flow grid, so positions
    // 1,3,5,7 are the left column (top to bottom) and 2,4,6,8 are the right.
    chem_only: [
      'Test and balance water chemistry',
      'Empty skimmer and pump baskets',
      'Inspect equipment for issues',
      'Photo report with technician notes emailed after every visit',
    ],
    chem_brush: [
      'Test and balance water chemistry',
      'Brush walls, steps, and tile',
      'Inspect equipment for issues',
      'Empty skimmer and pump baskets',
      'Photo report with technician notes emailed after every visit',
    ],
    full: [
      'Test and balance water chemistry',
      'Brush walls, steps, and tile',
      'Skim surface debris',
      'Vacuum pool surfaces',
      'Full equipment inspection',
      'Backwash filter when needed',
      'Empty skimmer and pump baskets',
      'Photo report with technician notes emailed after every visit',
    ],
    spa_only: [
      'Test and balance spa water chemistry',
      'Brush spa walls and steps as needed',
      'Inspect spa jets and equipment',
      'Photo report with technician notes emailed after every visit',
    ],
  },

  // Service-option picker lists. Rows line up across the side-by-side cards:
  // items every tier includes come first in the same order, then the items
  // that only some tiers include, so customers can compare row by row.
  tierPickerIncludes: {
    chem_only: [
      'Test and balance water chemistry',
      'Empty skimmer and pump baskets',
      'Standard chemicals included',
      'Inspect equipment for issues',
      'Photo report with technician notes emailed after every visit',
    ],
    chem_brush: [
      'Test and balance water chemistry',
      'Empty skimmer and pump baskets',
      'Standard chemicals included',
      'Inspect equipment for issues',
      'Photo report with technician notes emailed after every visit',
      'Brush walls, steps, and tile',
    ],
    full: [
      'Test and balance water chemistry',
      'Empty skimmer and pump baskets',
      'Standard chemicals included',
      'Full equipment inspection',
      'Photo report with technician notes emailed after every visit',
      'Brush walls, steps, and tile',
      'Skim surface debris',
      'Vacuum pool surfaces',
      'Backwash filter when needed',
    ],
  },

  // One line per tier so customers can tell which service fits them.
  tierPickerBestFor: {
    chem_only: 'Best if you skim, brush, and vacuum yourself and want the water chemistry handled.',
    chem_brush: 'Best if you want balanced water and clean walls, and are happy to skim and vacuum.',
    full: 'Best for completely hands-off weekly pool care.',
  },

  // Real 5-star reviews from Sacramento-area customers (cabana-cac.customers.reviews),
  // shown on the service-option picker, which only runs in the Sacramento region.
  sacReviews: [
    {
      name: 'Kim J.',
      source: 'Google',
      text: "In the past with another service not all things were being done as stated but DJ and Cabana has always done what's expected to get done and I always get an update after with what was done and a picture. My pool has never looked better!",
    },
    {
      name: 'Chad S.',
      source: 'Google',
      text: "Sean has been cleaning our pool for over a year now. Prior to Sean, we didn't have an entire summer without algae. Since, he's been our guy, the pool has been sparkling clean. The best part is that I don't even have to think about my pool as a chore anymore :)",
    },
    {
      name: 'Nancy P.',
      source: 'Google',
      text: 'Ron Cody Pool Service has served us tremendously well for the last 28 years. Weekly service is reliable. Whenever I have a question, Ron responds within minutes.',
    },
  ],

  serviceTierLabels: {
    chem_only: 'Chemicals Only',
    chem_brush: 'Chemicals and Brush',
    full: 'Full Service',
    spa_only: 'Spa Service',
  },

  cancelAnytimeText: 'Cancel anytime, no contract.',

  chemicalsNote: 'Maintenance chemicals (chlorine, acid) included. Specialty treatments quoted separately.',
  spaChemicalsNote: 'Maintenance chemicals (bromine, acid) included. Specialty treatments quoted separately.',
  saltSystemNote: 'We maintain your salt cell as part of regular service.',

  socialProofTotal: '5,000+',
  socialProofText: 'pools serviced across California',

  // Reviews shown on the recurring monthly-service quote variant.
  reviews: [
    {
      name: 'Maria G.',
      location: 'San Diego, CA',
      stars: 5,
      text: 'Switched from another company and the difference is night and day. The photo reports after every visit give me total peace of mind.',
    },
    {
      name: 'David L.',
      location: 'Sacramento, CA',
      stars: 5,
      text: 'They showed up when they said they would, the pool looks amazing, and the price is fair. Three months in and I couldn\'t be happier.',
    },
    {
      name: 'Jennifer R.',
      location: 'Los Angeles, CA',
      stars: 5,
      text: 'We\'ve had pool guys ghost us twice before. Cabana has been completely reliable and their customer service actually picks up the phone.',
    },
  ],

  // Reviews shown on the one-time-only quote variant. These are
  // customers who hired Cabana for a single job (Green-to-Clean,
  // filter clean, salt cell service, spa drain, etc.) so the social
  // proof matches what the visitor is actually buying.
  oneTimeReviews: [
    {
      name: 'Brian T.',
      location: 'San Diego, CA',
      stars: 5,
      text: 'Came out the same week for a Green-to-Clean and had the pool sparkling in 4 days. Texted me before they arrived and sent before/after photos.',
    },
    {
      name: 'Aileen M.',
      location: 'Los Angeles, CA',
      stars: 5,
      text: 'Filter cleaning was painless. They confirmed the date in advance, did the work in under 2 hours, and emailed a photo report when done. Fair price, no upsell.',
    },
    {
      name: 'Greg P.',
      location: 'Sacramento, CA',
      stars: 5,
      text: 'Booked a one-time service after our regular guy ghosted. Cabana was on time, professional, and the pool was the cleanest it had been in months.',
    },
  ],

  timeline: [
    { step: '1', title: 'Confirmation', description: 'We confirm your start date within 48 hours' },
    { step: '2', title: 'First Service', description: 'First visit usually within 5 business days' },
    { step: '3', title: 'Weekly Visits', description: 'Same day each week, consistent technician' },
  ],

  oneTimeTimeline: [
    { step: '1', title: 'Confirmation', description: 'We confirm your service date within 48 hours' },
    { step: '2', title: 'Service Date', description: 'Usually within 5 business days' },
    { step: '3', title: 'Photo Report', description: 'Emailed after the visit so you can see the finished pool' },
  ],

  terms: {
    monthly: [
      'Month-to-month; cancel anytime with 30 days notice.',
      'Billed on the 1st (4 visits/month; 5th-week months billed 25% extra).',
      'Prorated first month.',
      'We credit 25% for any missed week.',
      'Weather skips balance out over the year.',
      'Not included: equipment repairs, plaster/tile damage, or issues from insufficient filtration (8+ hrs/day summer, 5+ winter).',
    ],
    oneTime: [
      'This covers only the one-time services listed in this proposal.',
      'Payment due upon completion via invoice.',
      'Please ensure gate access and clear the pool area before our visit.',
      'Not included: equipment repairs, plaster/tile damage.',
    ],
  },

  // Plain-English service-agreement clauses shown as visible cards on
  // the quote page, above the consent checkbox. Each card has a short
  // summary (1-2 sentences) plus the longer "fine" wording inline.
  // Customers see every clause without having to expand anything; the
  // single checkbox below covers all of these as one clickwrap.
  // Source of truth lives in api/_lib/agreement-clauses.js so the
  // server-side submit handler can persist a snapshot at sign time.
  serviceAgreement: SERVICE_AGREEMENT,
};

// Per-brand overrides. Every brand inherits the full Cabana experience
// (features, pricing flow, agreement, copy, colors) and changes only what
// differs: display name, logo, phone, attribution (siteOrigin drives the Sell
// source_id, pricingBrand drives the engine price), the canonical host(s) that
// map to this brand, and the "now part of Cabana" acquisition popup. Service
// area for CA1, CA2, and CA3 is shared across brand hosts. CA4 is the East
// Bay partner handoff and is accepted only on the Cabana quote host.
//
// Resolution order: explicit ?brand= (preview/testing) then host, then cabana.
const BRAND_OVERRIDES = {
  cabana: {
    key: 'cabana',
    ...BRAND_MANIFEST.cabana,
    pricingBrand: 'Cabana',
    acquisitionPopup: null,
  },
  rancho: {
    key: 'rancho',
    ...BRAND_MANIFEST.rancho,
    name: 'Rancho Pool Service',
    tagline: 'Weekly Pool Service by Rancho, a Cabana company',
    website: 'ranchopools.com',
    pricingBrand: 'rancho',
    acquisitionPopup: {
      brandName: 'Rancho Pool Service',
      brandLogo: '/images/brands/rancho.svg',
    },
  },
  poollogic: {
    key: 'poollogic',
    ...BRAND_MANIFEST.poollogic,
    name: 'PoolLogic',
    tagline: 'Weekly Pool Service by PoolLogic, a Cabana company',
    website: 'poollogicsd.com',
    pricingBrand: 'Cabana',
    acquisitionPopup: {
      brandName: 'PoolLogic',
      brandLogo: '/images/brands/poollogic.svg',
    },
  },
  codys: {
    key: 'codys',
    ...BRAND_MANIFEST.codys,
    name: "Cody's Pool Service",
    tagline: "Weekly Pool Service by Cody's, a Cabana company",
    website: 'codyspoolservice.com',
    pricingBrand: 'codys',
    acquisitionPopup: {
      brandName: "Cody's Pool Service",
      brandLogo: '/images/brands/codys.svg',
    },
  },
};

const DEFAULT_BRAND = MANIFEST_DEFAULT;

// Each brand config is the full Cabana base with the brand overrides applied.
const BRANDS = Object.fromEntries(
  Object.entries(BRAND_OVERRIDES).map(([k, o]) => [k, { ...cabanaConfig, ...o }])
);

export function resolveBrandKey(search, hostname) {
  try {
    const qs = search != null
      ? search
      : (typeof window !== 'undefined' ? window.location.search : '');
    const brandParam = new URLSearchParams(qs).get('brand') || '';

    const host = hostname != null
      ? hostname
      : (typeof window !== 'undefined' ? window.location.hostname : '');

    return manifestResolveBrandKey(brandParam, host);
  } catch (e) {
    return DEFAULT_BRAND;
  }
}

// In-app step URLs drop the query string, so a ?brand= override honored on a
// preview host would fall back to the default brand on reload. Carry it onto
// the step path. Known brand hosts ignore ?brand=, so their paths are unchanged.
export function withPreviewBrand(path, search, hostname) {
  const qs = search != null ? search : window.location.search;
  const host = hostname != null ? hostname : window.location.hostname;
  if (resolveBrandFromHost(host)) return path;
  const key = validateBrandParam(new URLSearchParams(qs).get('brand'));
  return key ? `${path}?brand=${key}` : path;
}

export function getBrandConfig(key) {
  return BRANDS[key] || BRANDS[DEFAULT_BRAND];
}

// Active config for this page load, resolved from host / ?brand=. Components
// still receive this as a prop, so the default export stays drop-in compatible
// with the previous single-brand cabanaConfig.
const activeConfig = getBrandConfig(resolveBrandKey());

export { BRANDS, BRAND_OVERRIDES, DEFAULT_BRAND };
export default activeConfig;
